'use client';

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Plus,
  MagnifyingGlass,
  DotsThree,
  Lock,
  UserMinus,
  UserPlus,
  Trash,
  X,

  Spinner,
} from '@phosphor-icons/react';
import { userApi } from '../services/api';
import { useToastActions } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useConfirm } from '../components/ui/ConfirmDialog';

interface User {
  id: string;
  username: string;
  email?: string;
  avatar?: string;
  role?: string;
  status?: 'active' | 'inactive' | 'locked';
  createdAt?: string;
}

// 添加用户对话框
function AddUserDialog({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { username: string; email: string; password: string; role: 'admin' | 'user' }) => Promise<void>;
}) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user' as 'admin' | 'user',
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToastActions();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.email || !formData.password) {
      toast.error('请填写所有必填字段');
      return;
    }
    setIsLoading(true);
    try {
      await onSubmit(formData);
      setFormData({ username: '', email: '', password: '', role: 'user' });
      onClose();
    } catch (err) {
      // 错误已在父组件处理
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-zinc-900">添加用户</h3>
            <button
              onClick={onClose}
              className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <Input
              label="用户名"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="请输入用户名"
              required
            />
            <Input
              label="邮箱"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="请输入邮箱"
              required
            />
            <Input
              label="密码"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="请输入密码（至少6位）"
              required
            />
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">角色</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'user' })}
                  className={`flex-1 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    formData.role === 'user'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  普通用户
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'admin' })}
                  className={`flex-1 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    formData.role === 'admin'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  管理员
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
                取消
              </Button>
              <Button type="submit" variant="primary" isLoading={isLoading} className="flex-1">
                创建用户
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// 重置密码对话框
function ResetPasswordDialog({
  isOpen,
  onClose,
  onSubmit,
  username,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
  username: string;
}) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      return;
    }
    setIsLoading(true);
    try {
      await onSubmit(password);
      setPassword('');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-zinc-100">
            <h3 className="text-lg font-semibold text-zinc-900">重置密码</h3>
            <p className="text-sm text-zinc-500 mt-1">为用户 <span className="font-medium text-zinc-700">{username}</span> 设置新密码</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <Input
              label="新密码"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="请输入新密码（至少6位）"
              required
            />

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
                取消
              </Button>
              <Button type="submit" variant="primary" isLoading={isLoading} className="flex-1">
                确认重置
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  const toast = useToastActions();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userApi.listUsers({
        page,
        pageSize,
        keyword: searchKeyword || undefined,
      });
      if (response.code === 0 && response.data) {
        setUsers(response.data.users as User[]);
        setTotal(response.data.total);
      } else {
        toast.error(response.message || '获取用户列表失败');
      }
    } catch (err) {
      toast.error('获取用户列表失败');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, searchKeyword]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (data: { username: string; email: string; password: string; role: 'admin' | 'user' }) => {
    try {
      const response = await userApi.createUser(data);
      if (response.code === 0) {
        toast.success('用户创建成功');
        fetchUsers();
      } else {
        toast.error(response.message || '创建用户失败');
        throw new Error(response.message);
      }
    } catch (err) {
      toast.error('创建用户失败');
      throw err;
    }
  };

  const handleResetPassword = async (password: string) => {
    if (!resetPasswordUser) return;
    try {
      const response = await userApi.resetPassword(resetPasswordUser.id, password);
      if (response.code === 0) {
        toast.success('密码重置成功');
      } else {
        toast.error(response.message || '密码重置失败');
      }
    } catch (err) {
      toast.error('密码重置失败');
    }
  };

  const handleToggleStatus = async (user: User) => {
    const isDisabling = user.status === 'active';
    const confirmed = await confirm({
      title: isDisabling ? '停用用户' : '启用用户',
      description: `确定要${isDisabling ? '停用' : '启用'}用户 "${user.username}" 吗？`,
      confirmText: isDisabling ? '停用' : '启用',
      cancelText: '取消',
      type: isDisabling ? 'danger' : 'info',
    });

    if (!confirmed) return;

    try {
      const response = await userApi.toggleUserStatus(user.id);
      if (response.code === 0) {
        toast.success(isDisabling ? '用户已停用' : '用户已启用');
        fetchUsers();
      } else {
        toast.error(response.message || '操作失败');
      }
    } catch (err) {
      toast.error('操作失败');
    }
  };

  const handleDeleteUser = async (user: User) => {
    const confirmed = await confirm({
      title: '删除用户',
      description: `确定要删除用户 "${user.username}" 吗？此操作不可撤销。`,
      confirmText: '删除',
      cancelText: '取消',
      type: 'danger',
    });

    if (!confirmed) return;

    try {
      const response = await userApi.deleteUser(user.id);
      if (response.code === 0) {
        toast.success('用户已删除');
        fetchUsers();
      } else {
        toast.error(response.message || '删除失败');
      }
    } catch (err) {
      toast.error('删除失败');
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">正常</span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">已停用</span>;
      case 'locked':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">已锁定</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">未知</span>;
    }
  };

  const getRoleBadge = (role?: string) => {
    switch (role) {
      case 'admin':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">管理员</span>;
      case 'user':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">普通用户</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">未知</span>;
    }
  };

  return (
    <div className="h-full flex flex-col bg-zinc-50/50">
      {confirmDialog}
      
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-zinc-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" weight="fill" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-zinc-900">用户管理</h1>
              <p className="text-sm text-zinc-500">管理系统用户账号</p>
            </div>
          </div>
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowAddDialog(true)}>
            添加用户
          </Button>
        </div>

        {/* Search */}
        <div className="mt-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="搜索用户名或邮箱..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* User List */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner className="w-8 h-8 text-emerald-500 animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-zinc-400">
            <Users className="w-12 h-12 mb-3" weight="light" />
            <p>暂无用户</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">用户</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">角色</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">创建时间</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-zinc-50/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-sm font-medium text-zinc-600">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-zinc-900">{user.username}</p>
                          <p className="text-xs text-zinc-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getRoleBadge(user.role)}</td>
                    <td className="px-4 py-3">{getStatusBadge(user.status)}</td>
                    <td className="px-4 py-3 text-sm text-zinc-500">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('zh-CN') : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            if (activeMenu === user.id) {
                              setActiveMenu(null);
                              setMenuPosition(null);
                            } else {
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              const menuHeight = 140;
                              const spaceBelow = window.innerHeight - rect.bottom;
                              const spaceAbove = rect.top;
                              const openAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow;
                              setMenuPosition({
                                top: openAbove ? rect.top - menuHeight - 4 : rect.bottom + 4,
                                right: window.innerWidth - rect.right,
                              });
                              setActiveMenu(user.id);
                            }
                          }}
                          className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
                        >
                          <DotsThree className="w-5 h-5" weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Operations menu - render via portal to avoid overflow clipping */}
            {activeMenu && menuPosition && (() => {
              const user = users.find((u) => u.id === activeMenu);
              if (!user) return null;
              return createPortal(
                <AnimatePresence>
                  <div
                    className="fixed inset-0 z-[100]"
                    onClick={() => { setActiveMenu(null); setMenuPosition(null); }}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed w-40 bg-white rounded-lg shadow-lg border border-zinc-200 py-1 z-[110]"
                    style={{
                      top: menuPosition.top,
                      right: menuPosition.right,
                    }}
                  >
                    <button
                      onClick={() => {
                        setResetPasswordUser(user);
                        setActiveMenu(null);
                        setMenuPosition(null);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-2"
                    >
                      <Lock className="w-4 h-4" />
                      重置密码
                    </button>
                    <button
                      onClick={() => {
                        handleToggleStatus(user);
                        setActiveMenu(null);
                        setMenuPosition(null);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-2"
                    >
                      {user.status === 'active' ? (
                        <>
                          <UserMinus className="w-4 h-4" />
                          停用用户
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4" />
                          启用用户
                        </>
                      )}
                    </button>
                    <div className="h-px bg-zinc-100 my-1" />
                    <button
                      onClick={() => {
                        handleDeleteUser(user);
                        setActiveMenu(null);
                        setMenuPosition(null);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash className="w-4 h-4" />
                      删除用户
                    </button>
                  </motion.div>
                </AnimatePresence>,
                document.body
              );
            })()}

            {/* Pagination */}
            {total > pageSize && (
              <div className="px-4 py-3 border-t border-zinc-200 flex items-center justify-between">
                <p className="text-sm text-zinc-500">
                  共 {total} 条记录
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  <span className="text-sm text-zinc-500">
                    第 {page} 页
                  </span>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * pageSize >= total}
                    className="px-3 py-1.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddUserDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSubmit={handleCreateUser}
      />
      <ResetPasswordDialog
        isOpen={!!resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
        onSubmit={handleResetPassword}
        username={resetPasswordUser?.username || ''}
      />
    </div>
  );
}
